import React from 'react';
import { useTranslation } from 'react-i18next';
import { required } from '../../../../../../antd/form/FormValidation.js';
import FormButton from '../../../../../../antd/form/FormButton/FormButton.jsx';
import FormButtonsContainer from '../../../../../../antd/form/FormButtonsContainer/FormButtonsContainer.jsx';
import FormHiddenField from '../../../../../../antd/form/FormHiddenField/FormHiddenField.jsx';
import Form from '../../../../../../antd/form/Form/Form.jsx';
import PersonalData from '../PersonalData/PersonalData.jsx';
import RecievingInstitution from '../RecievingInstitution/RecievingInstitution.jsx';
import RequiredDocuments from '../RequiredDocuments/RequiredDocuments.jsx';
import { CollapseBlock } from '../../../../../../common/index.jsx';
import { Form as AntdForm, Grid, Progress, Select, Tag } from 'antd'; // Import Ant Design Form explicitly for hidden fields
import PreviousMobilities from '../PreviousMobilities/PreviousMobilities';
import { useGetErasmusMobilityTypesQuery } from '../../../../../../../redux/services/erasmusMobilityTypes';
import { useGetErasmusMobilitySubtypesQuery } from '../../../../../../../redux/services/erasmusMobilitySubtypes';
import { useGetErasmusChallengesQuery } from '../../../../../../../redux/services/erasmusChallenges';
import { Spinner } from '../../../../../../common/index';
import { useEffect, useMemo, useState } from 'react';
import { useGetStudyProgramsQuery } from '../../../../../../../redux/services/study_programs';
import axios from 'axios';
import dayjs from 'dayjs';
import { useLazyGetErasmusPrivateFileQuery } from '../../../../../../../redux/services/erasmusApplications';

const ErasmusApplicationForm = ({
  onSubmit,
  initials,
  isEdit,
  isPreview,
  isAdmin,
}) => {
  const { t } = useTranslation();
  const [form] = AntdForm.useForm();
  const screens = Grid.useBreakpoint();
  const { data: erasmusMobilityTypes, isTypesLoading } =
    useGetErasmusMobilityTypesQuery();
  const { data: erasmusMobilitySubtypes, isSubtypesLoading } =
    useGetErasmusMobilitySubtypesQuery();
  const { data: erasmusChallenges, isChallengesLoading } =
    useGetErasmusChallengesQuery();
  const [isInitValuesReady, setInitValuesReady] = useState(true);
  const [initialValues, setinitialValues] = useState({ ...initials });
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const { data: studyPrograms, isLoading: isStudyProgramsLoading } =
    useGetStudyProgramsQuery();
  //const students = initials?.person?.students?.[0] ?? null; // ?? initials?.person?.activeStudent;
  const students = Array.isArray(initials?.person?.students)
    ? (initials.person.students[initials.person.students.length - 1] ?? null)
    : (initials?.person?.student ?? null);

  const [current_study_group, set_study_group] = useState();
  const updateStudyProgram = (newProgram) => {
    setinitialValues((initials) => ({
      ...initials,
      study_program: newProgram,
    }));
  };
  const [languages, setLanguages] = useState([]);
  const [triggerDownload] = useLazyGetErasmusPrivateFileQuery();

  useEffect(() => {
    axios
      .get('/api/languages-erasmus')
      .then((response) => {
        setLanguages(response.data);
      })
      .catch((error) => {
        console.error('Error fetching languages:', error);
      });
  }, []);

  const formatDaysLeft = (daysLeft) => {
    if (daysLeft === 1) return t('1 deň');
    if (daysLeft >= 2 && daysLeft <= 4)
      return t('{{count}} dni', { count: daysLeft });
    return t('{{count}} dní', { count: daysLeft });
  };

  const getDeadlineMeta = (deadline) => {
    if (!deadline) {
      return { label: t('Bez termínu'), color: 'default', daysLeft: null };
    }

    const parsed = dayjs(deadline);
    if (!parsed.isValid()) {
      return { label: '-', color: 'default', daysLeft: null };
    }

    const today = dayjs().startOf('day');
    const endOfDeadline = parsed.endOf('day');
    const daysLeft = endOfDeadline.diff(today, 'day');

    if (daysLeft < 0)
      return { label: t('Ukončená'), color: 'default', daysLeft };
    if (daysLeft === 0) return { label: t('Dnes'), color: 'red', daysLeft };
    if (daysLeft < 3)
      return { label: formatDaysLeft(daysLeft), color: 'red', daysLeft };
    if (daysLeft < 14)
      return { label: formatDaysLeft(daysLeft), color: 'orange', daysLeft };
    return { label: formatDaysLeft(daysLeft), color: 'blue', daysLeft };
  };

  const getDeadlineProgress = (deadline) => {
    if (!deadline) return null;
    const parsed = dayjs(deadline);
    if (!parsed.isValid()) return null;

    const today = dayjs().startOf('day');
    const endOfDeadline = parsed.endOf('day');
    const daysLeft = endOfDeadline.diff(today, 'day');
    if (daysLeft < 0) return { percent: 100, color: '#595959' };

    const totalWindowDays = 30;
    const clampedLeft = Math.min(Math.max(daysLeft, 0), totalWindowDays);
    const percent = Math.round(
      ((totalWindowDays - clampedLeft) / totalWindowDays) * 100,
    );

    let color = '#1677ff';
    if (daysLeft < 3) color = '#ff4d4f';
    else if (daysLeft < 14) color = '#fa8c16';

    return { percent, color };
  };

  const studentCriteria = useMemo(() => {
    if (!students?.school_group || !studyPrograms) return null;

    const group = String(students.school_group).toUpperCase();
    if (group.length < 5) return null;

    const fieldCode = group[2];
    const programCode = group[3];
    const yearCode = group[4];
    const wsCode =
      fieldCode && programCode ? `${fieldCode}${programCode}` : null;
    const studyProgram = wsCode
      ? studyPrograms.find(
          (opt) => String(opt.ws_code).toUpperCase() === wsCode,
        )
      : null;

    let studyDegree = null;
    if (['Y', 'M'].includes(fieldCode)) studyDegree = 'Bc.';
    if (['I', 'E'].includes(fieldCode)) studyDegree = 'Ing.';

    const parsedYear = Number.parseInt(yearCode, 10);
    const studyYear = Number.isNaN(parsedYear) ? null : parsedYear;

    return {
      studyProgramId: studyProgram?.id ?? null,
      studyDegree,
      studyYear,
      wsCode,
    };
  }, [students, studyPrograms]);

  const eligibleChallenges = useMemo(() => {
    if (!erasmusChallenges) return [];
    if (isEdit || isPreview || isAdmin) return erasmusChallenges;
    if (!studentCriteria) return erasmusChallenges;

    const today = dayjs().startOf('day');
    return erasmusChallenges.filter((challenge) => {
      if (challenge.deadline) {
        const deadline = dayjs(challenge.deadline).endOf('day');
        if (deadline.isBefore(today)) return false;
      }
      if (challenge.study_year != null) {
        if (!studentCriteria.studyYear) return false;
        if (Number(challenge.study_year) !== Number(studentCriteria.studyYear))
          return false;
      }
      if (challenge.study_degree) {
        if (!studentCriteria.studyDegree) return false;
        if (challenge.study_degree !== studentCriteria.studyDegree)
          return false;
      }
      if (challenge.study_program_id != null) {
        if (!studentCriteria.studyProgramId) return false;
        if (
          Number(challenge.study_program_id) !==
          Number(studentCriteria.studyProgramId)
        )
          return false;
      }
      return true;
    });
  }, [erasmusChallenges, isEdit, isPreview, isAdmin, studentCriteria]);

  // Options for challenges - replace mobility type selection
  const challengeById = useMemo(() => {
    if (!erasmusChallenges) return new Map();
    return new Map(
      erasmusChallenges.map((challenge) => [challenge.id, challenge]),
    );
  }, [erasmusChallenges]);

  const challengeOptions = useMemo(() => {
    if (!eligibleChallenges) return [];
    return eligibleChallenges.map((challenge) => ({
      value: challenge.id,
      label: `${challenge.name} (${challenge.academic_year})`,
      searchLabel: [
        challenge.name,
        challenge.academic_year,
        challenge.university?.name,
        challenge.consortium?.name,
      ]
        .filter(Boolean)
        .join(' '),
    }));
  }, [eligibleChallenges]);

  // Keep old mobility types for backward compatibility (if needed)
  const mobTypesOptions = useMemo(() => {
    if (!erasmusMobilityTypes || !erasmusMobilitySubtypes) return [];

    return erasmusMobilityTypes.flatMap((data) => {
      const relatedSubs = erasmusMobilitySubtypes.filter(
        (sub) => sub.type_id === data.id,
      );

      // ak existujú subtypy, vytvoríme viac záznamov pre jeden typov
      return relatedSubs.length > 0
        ? relatedSubs.map((sub) => ({
            label: `${data.name} (${sub.name})`, // Použitie name z subTipov
            value: JSON.stringify({
              mobility_type: data.id,
              mobility_sub_type: sub.id,
            }),
          }))
        : [
            {
              label: `${data.name}`, //ak nemá subtypy, zobrazí základnú hodnotu
              value: JSON.stringify({
                mobility_type: data.id,
                mobility_sub_type: null,
              }),
            },
          ];
    });
  }, [erasmusMobilityTypes, erasmusMobilitySubtypes]);

  // Set selected challenge when editing/previewing
  useEffect(() => {
    if (isEdit || isPreview) {
      if (initials.challenge_id && erasmusChallenges) {
        const challenge = erasmusChallenges.find(
          (c) => c.id === initials.challenge_id,
        );
        if (challenge) {
          setSelectedChallenge(challenge);
        }
      } else {
        setSelectedChallenge(null);
      }
      setInitValuesReady(true);
    }
  }, [isEdit, isPreview, initials, erasmusChallenges]);

  // Handle challenge selection change
  const handleChallengeChange = (challengeId) => {
    form.setFieldsValue({
      university1_id: undefined,
      university2_id: undefined,
      university3_id: undefined,
      choosed_university_id: undefined,
      requested_choosed_university_id: undefined,
    });

    if (challengeId && erasmusChallenges) {
      const challenge = erasmusChallenges.find((c) => c.id === challengeId);
      setSelectedChallenge(challenge);
      form.setFieldsValue({ challenge_id: challengeId });
    } else {
      setSelectedChallenge(null);
      form.setFieldsValue({ challenge_id: null });
    }
  };

  useEffect(() => {
    if (studyPrograms && students) {
      if (!(students.length === 0)) {
        set_study_group(students.school_group);
        const selectedOption = studyPrograms.find((opt) => {
          //TODO: Add if it is a admin - admin does not have students, problem with school_group :(

          return opt.ws_code === students.school_group.slice(2, 4);
        });

        if (selectedOption) {
          updateStudyProgram(selectedOption.id);
        } else {
          // neblokuj formulár, ak sa nenašlo mapovanie
          updateStudyProgram(undefined);
        }
      } else {
        updateStudyProgram(null);
      }
    }
  }, [studyPrograms, students]);

  const handlePreview = async (file) => {
    try {
      const applicationId = initialValues.id;
      const filename = file.name;

      const result = await triggerDownload(
        {
          id: applicationId,
          filename,
        },
        true,
      );

      //recognize file type by extension
      const ext = filename.split('.').pop().toLowerCase();
      let mimeType = 'application/pdf';
      if (['jpg', 'jpeg'].includes(ext)) mimeType = 'image/jpeg';
      if (ext === 'png') mimeType = 'image/png';

      const byteCharacters = atob(result.data.pdf);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);

      const blob = new Blob([byteArray], { type: mimeType });
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
    } catch (error) {
      console.error('Preview error:', error);
    }
  };

  return (
    <Form onFinish={onSubmit} initialValues={initialValues} form={form}>
      <AntdForm.Item
        label={t('Výzva')}
        name={'challenge_id'}
        rules={[required(t)]}
      >
        <Select
          loading={isChallengesLoading}
          options={challengeOptions}
          disabled={isPreview || (isEdit && !isAdmin)}
          onChange={handleChallengeChange}
          placeholder={t('Vybrať výzvu')}
          allowClear
          showSearch
          optionLabelProp="label"
          optionFilterProp="searchLabel"
          filterOption={(input, option) =>
            (option?.searchLabel ?? '')
              .toLowerCase()
              .includes(input.toLowerCase())
          }
          popupMatchSelectWidth={true}
          dropdownRender={(menu) => (
            <div>
              {screens.md && (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns:
                      'minmax(0, 2.2fr) minmax(0, 1fr) minmax(0, 1.4fr) minmax(0, 1.4fr)',
                    gap: '12px',
                    padding: '8px 12px',
                    fontSize: '12px',
                    color: '#8c8c8c',
                    borderBottom: '1px solid #f0f0f0',
                  }}
                >
                  <span>{t('Názov')}</span>
                  <span>{t('Termín')}</span>
                  <span>{t('Konzorcium/Univerzita')}</span>
                  <span>{t('Deadline')}</span>
                </div>
              )}
              {menu}
            </div>
          )}
          optionRender={(item) => {
            const challenge = challengeById.get(item.value) || item.data || {};
            const meta = getDeadlineMeta(challenge.deadline);
            const progress = getDeadlineProgress(challenge.deadline);
            const deadlineLabel = challenge.deadline
              ? dayjs(challenge.deadline).format('D.M.YYYY')
              : '-';
            const relationLabel =
              challenge.consortium?.name || challenge.university?.name || '-';

            if (!screens.md) {
              return (
                <div style={{ minWidth: 0, width: '100%', padding: '4px 0' }}>
                  <div
                    title={challenge.name || '-'}
                    style={{
                      display: '-webkit-box',
                      overflow: 'hidden',
                      WebkitBoxOrient: 'vertical',
                      WebkitLineClamp: 2,
                      whiteSpace: 'normal',
                      overflowWrap: 'anywhere',
                      fontWeight: 500,
                      lineHeight: 1.35,
                    }}
                  >
                    {challenge.name || '-'}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: '12px',
                      marginTop: '4px',
                      color: '#8c8c8c',
                      fontSize: '12px',
                    }}
                  >
                    <span>{challenge.academic_year || '-'}</span>
                    <span style={{ whiteSpace: 'nowrap' }}>
                      {t('Termín')}: {deadlineLabel}
                    </span>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-end',
                      justifyContent: 'space-between',
                      gap: '8px',
                      marginTop: '8px',
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ color: '#8c8c8c', fontSize: '11px' }}>
                        {t('Konzorcium/Univerzita')}
                      </div>
                      <div
                        title={relationLabel}
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {relationLabel}
                      </div>
                    </div>
                    {meta.label !== '-' && (
                      <Tag
                        color={meta.color}
                        style={{ flexShrink: 0, marginInlineEnd: 0 }}
                      >
                        {meta.label}
                      </Tag>
                    )}
                  </div>
                </div>
              );
            }

            return (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns:
                    'minmax(0, 2.2fr) minmax(0, 1fr) minmax(0, 1.4fr) minmax(0, 1.4fr)',
                  gap: '12px',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div
                    title={challenge.name || '-'}
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontWeight: 500,
                    }}
                  >
                    {challenge.name || '-'}
                  </div>
                  <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                    {challenge.academic_year || '-'}
                  </div>
                </div>
                <div style={{ whiteSpace: 'nowrap' }}>{deadlineLabel}</div>
                <div
                  title={relationLabel}
                  style={{
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {relationLabel}
                </div>
                <div style={{ minWidth: '120px' }}>
                  {meta.label !== '-' && (
                    <Tag color={meta.color}>{meta.label}</Tag>
                  )}
                  {progress && (
                    <Progress
                      percent={progress.percent}
                      size="small"
                      showInfo={false}
                      strokeColor={progress.color}
                    />
                  )}
                </div>
              </div>
            );
          }}
        />
      </AntdForm.Item>
      <AntdForm.Item name="person_id" hidden>
        <input type="hidden" />
      </AntdForm.Item>
      <FormHiddenField name={'mobility_type'} />
      <FormHiddenField name={'mobility_subtype'} />
      <CollapseBlock title={t('Osobné údaje')} collapsible="false">
        <PersonalData
          initials={initialValues}
          form={form}
          updateStudyProgram={updateStudyProgram}
          studyPrograms={studyPrograms}
          study_group={current_study_group}
          isPreview={isPreview}
          onPreview={(file) => {
            handlePreview(file);
          }}
          isAdmin={isAdmin}
        />
      </CollapseBlock>
      <CollapseBlock title={t('Prijímajúca inštitúcia')} collapsible="false">
        <RecievingInstitution
          languages={languages}
          isPreview={isPreview}
          selectedChallenge={selectedChallenge}
        />
      </CollapseBlock>
      <CollapseBlock title={t('Predchádzajúce mobility')} collapsible="false">
        <PreviousMobilities initials={initialValues} />
      </CollapseBlock>
      <CollapseBlock title={t('Prílohy')} collapsible="false">
        <RequiredDocuments
          onPreview={(file) => {
            handlePreview(file);
          }}
          isPreview={isPreview}
        />
      </CollapseBlock>
      <FormButtonsContainer>
        {isPreview ? null : (
          <FormButton submitOnClick={true} submitKey={'erasmusApplications'}>
            {t('Uložiť')}
          </FormButton>
        )}
      </FormButtonsContainer>
    </Form>
  );
};

export default ErasmusApplicationForm;
